import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Plus, Trash2, ChevronRight, Loader, X, Check, Pencil,
  BookOpen, Layers, FileText, CloudUpload, Eye, Play, Upload, Search,
} from "lucide-react";
import {
  fetchLmsSchoolClasses,
  fetchLmsClasses,
  fetchLmsClassSubjects,
  fetchLmsChapters,
  fetchLmsTopics,
  fetchLmsMaterials,
  setSelectedClass,
  setSelectedClassSubject,
  setSelectedChapter,
  setSelectedTopic,
  addSubjectOptimistic,
  addChapterOptimistic,
  addTopicOptimistic,
  removeMaterial,
} from "../redux/lmsSlice";
import {
  lmsGetClasses,
  lmsCreateClass,
  lmsUpdateClass,
  lmsGetSubjects,
  lmsGetChapters,
  lmsCreateSchoolClass,
  lmsDeleteSchoolClass,
  lmsCreateSubject,
  lmsUpdateSubject,
  lmsCreateClassSubject,
  lmsDeleteClassSubject,
  lmsCreateChapter,
  lmsUpdateChapter,
  lmsDeleteChapter,
  lmsGetTopics,
  lmsCreateTopic,
  lmsUpdateTopic,
  lmsDeleteTopic,
  lmsUploadMaterial,
  lmsAddVideoUrl,
  lmsUpdateMaterial,
  lmsDeleteMaterial,
} from "../api/lmsApi";
import "../lms.css";

/* ── helpers ── */
const getSchoolId = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}").admin_id || null; }
  catch { return null; }
};

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diff = Math.floor((Date.now() - d) / 86400000);
  if (diff === 0) return "Uploaded Today";
  if (diff === 1) return "Uploaded Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

const TYPE_BADGE = {
  pdf:   { label: "PDF",  bg: "#FEE2E2", color: "#991B1B" },
  video: { label: "MP4",  bg: "#DBEAFE", color: "#1E40AF" },
  image: { label: "IMG",  bg: "#FFEDD5", color: "#9A3412" },
};
function getBadge(type, fileName = "") {
  if (type === "pdf") {
    const ext = (fileName.split(".").pop() || "PDF").toUpperCase();
    return { label: ext, bg: "#FEE2E2", color: "#991B1B" };
  }
  return TYPE_BADGE[type] || { label: "FILE", bg: "#F3F4F6", color: "#6B7280" };
}

/* ── Stepper ── */
const STEPS = ["Class Selection", "Subject Selection", "Chapter Selection", "Topic Selection"];

function Stepper({ activeStep, breadcrumb }) {
  return (
    <div className="ms2-stepper">
      <div className="ms2-stepper-steps">
        {STEPS.map((label, i) => {
          const done = i < activeStep, active = i === activeStep;
          return (
            <React.Fragment key={label}>
              <div className="ms2-step">
                <div className={`ms2-step-circle ${active ? "active" : done ? "done" : ""}`}>{i + 1}</div>
                <span className={`ms2-step-label ${active ? "active" : done ? "done" : ""}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <ChevronRight size={14} color="#D1D5DB" style={{ flexShrink: 0 }} />}
            </React.Fragment>
          );
        })}
      </div>
      {breadcrumb && (
        <div className="ms2-breadcrumb">Selected: <span>{breadcrumb}</span></div>
      )}
    </div>
  );
}

/* ── Confirmation modal ── */
function ConfirmModal({ message, onConfirm, onCancel, loading }) {
  // Split message on "quoted text" and highlight it
  const parts = message.split(/("([^"]+)")/g);
  const rendered = parts.map((part, i) =>
    part.startsWith('"') && part.endsWith('"')
      ? <strong key={i} style={{ color: "#4f46e5", fontWeight: 700 }}>{part}</strong>
      : part
  );
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div className="ms2-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-confirm-icon">
          <Trash2 size={24} color="#dc2626" />
        </div>
        <p className="ms2-confirm-msg">{rendered}</p>
        <div className="ms2-confirm-footer">
          <button className="lms-btn-ghost" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="ms2-confirm-delete-btn" onClick={onConfirm} disabled={loading}>
            {loading ? <Loader size={13} className="ms2-spin" /> : <Trash2 size={13} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Simple text add modal (subject / chapter) ── */
function AddModal({ title, placeholder, onClose, onAdd, loading }) {
  const [val, setVal] = useState("");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = () => { const t = val.trim(); if (t) onAdd(t, () => { setVal(""); onClose(); }); };
  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-modal-header">
          <span>{title}</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <input ref={ref} className="ms2-modal-input" placeholder={placeholder}
          value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()} />
        <div className="ms2-modal-footer">
          <button className="lms-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="lms-btn-primary" onClick={submit} disabled={loading || !val.trim()}>
            {loading && <Loader size={13} className="ms2-spin" />} Add
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Class picker modal ── */
function ClassPickerModal({ schoolId, assignedClassIds, onClose, onSaved }) {
  const [allClasses, setAllClasses]     = useState([]);
  const [loadingAll, setLoadingAll]     = useState(true);
  const [selected, setSelected]         = useState(new Set(assignedClassIds));
  const [saving, setSaving]             = useState(false);
  const [newName, setNewName]           = useState("");
  const [addingNew, setAddingNew]       = useState(false);
  const [search, setSearch]             = useState("");

  useEffect(() => {
    lmsGetClasses()
      .then((r) => setAllClasses(r.data.data || []))
      .catch(() => toast.error("Failed to load classes"))
      .finally(() => setLoadingAll(false));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    const toAdd    = [...selected].filter((id) => !assignedClassIds.includes(id));
    const toRemove = assignedClassIds.filter((id) => !selected.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) { onClose(); return; }

    setSaving(true);
    try {
      // Delegate ALL API calls to the parent handler
      await onSaved({ added: toAdd, removed: toRemove });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save class selection");
    } finally { setSaving(false); }
  };

  const handleAddNewGlobal = async () => {
    const t = newName.trim();
    if (!t) return;
    setAddingNew(true);
    try {
      const res = await lmsCreateClass({ name: t });
      const created = res.data.data;
      setAllClasses((prev) => [...prev, created]);
      setSelected((prev) => new Set([...prev, created.id]));
      setNewName("");
      toast.success(`Global class "${t}" created`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create class");
    } finally { setAddingNew(false); }
  };

  const filtered = allClasses.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header — gradient */}
        <div className="ms2-modal-header">
          <span>Select Classes for Your School</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="ms2-class-modal-body">
          {/* Search */}
          <div className="ms2-class-search">
            <Search size={15} />
            <input
              placeholder="Search classes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <p className="ms2-class-sel-count"><strong>{selected.size}</strong> selected</p>

          {/* Class grid — 4 columns */}
          <div className="ms2-class-list">
            {loadingAll ? (
              <div className="ms2-class-list-empty"><Loader size={16} className="ms2-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="ms2-class-list-empty">No classes found</div>
            ) : (
              filtered.map((c) => {
                const isSelected = selected.has(c.id);
                return (
                  <label
                    key={c.id}
                    className={`ms2-class-row${isSelected ? " ms2-class-row--selected" : ""}`}
                    onClick={() => toggle(c.id)}
                  >
                    <span className="ms2-class-check-icon">
                      {isSelected && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className="ms2-class-row-name">{c.name}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Add new global class */}
        <div className="ms2-class-new-section">
          <p className="ms2-class-new-label">Add a new global class (available to all schools)</p>
          <div className="ms2-class-new-row">
            <input
              placeholder="e.g. Class 12 - Science"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddNewGlobal()}
            />
            <button
              className="lms-btn-primary"
              onClick={handleAddNewGlobal}
              disabled={addingNew || !newName.trim()}
              style={{ flexShrink: 0 }}
            >
              {addingNew ? <Loader size={13} className="ms2-spin" /> : <Plus size={14} />}
              Create
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="ms2-modal-footer ms2-class-modal-footer">
          <span />
          <button className="lms-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="lms-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader size={13} className="ms2-spin" /> : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Subject Picker Modal ── */
const mkRow = () => ({ uid: Date.now() + Math.random(), name: "", desc: "" });

function SubjectPickerModal({ schoolId, classId, assignedSubjects, onClose, onSaved, onRefresh }) {
  const assignedSubjectIds = assignedSubjects.map((cs) => cs.subject_id).filter(Boolean);

  const [allSubjects, setAllSubjects] = useState([]);
  const [loadingAll, setLoadingAll]   = useState(true);
  const [selected, setSelected]       = useState(new Set(assignedSubjectIds));
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState("");
  const [creatingAll, setCreatingAll] = useState(false);
  const [rows, setRows]               = useState([mkRow()]);

  useEffect(() => {
    lmsGetSubjects(schoolId)
      .then((r) => setAllSubjects(r.data.data || []))
      .catch(() => toast.error("Failed to load subjects"))
      .finally(() => setLoadingAll(false));
  }, [schoolId]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const updateRow = (uid, field, val) =>
    setRows((prev) => prev.map((r) => r.uid === uid ? { ...r, [field]: val } : r));
  const addRow    = () => setRows((prev) => [...prev, mkRow()]);
  const removeRow = (uid) =>
    setRows((prev) => prev.length > 1 ? prev.filter((r) => r.uid !== uid) : [mkRow()]);

  const validRows = rows.filter((r) => r.name.trim());

  const handleCreateAll = async () => {
    if (!validRows.length) return;
    setCreatingAll(true);
    try {
      const results = await Promise.all(
        validRows.map((r) => lmsCreateSubject({ school_id: schoolId, name: r.name.trim(), description: r.desc.trim() || null }))
      );
      const created = results.map((res) => res.data.data).filter(Boolean);
      // Auto-assign created subjects to the class immediately
      await Promise.all(
        created.map((s) => lmsCreateClassSubject({ class_id: classId, subject_id: s.id, school_id: schoolId }))
      );
      setAllSubjects((prev) => [...prev, ...created]);
      setSelected((prev) => new Set([...prev, ...created.map((s) => s.id)]));
      setRows([mkRow()]);
      toast.success(`${created.length} subject${created.length > 1 ? "s" : ""} created & assigned to class`);
      // Refresh so "Assigned" badges update and diff stays correct
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to create subjects"); }
    finally { setCreatingAll(false); }
  };

  const handleSave = async () => {
    const toAdd    = [...selected].filter((id) => !assignedSubjectIds.includes(id));
    const toRemove = assignedSubjectIds.filter((id) => !selected.has(id));
    if (toAdd.length === 0 && toRemove.length === 0) { onClose(); return; }
    setSaving(true);
    try {
      await onSaved({ added: toAdd, removed: toRemove });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save subjects");
    } finally { setSaving(false); }
  };

  const filtered = allSubjects.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal ms2-subj-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ms2-modal-header">
          <span>Select Subjects for This Class</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Existing subjects */}
        <div className="ms2-class-modal-body">
          <div className="ms2-class-search">
            <Search size={15} />
            <input placeholder="Search subjects..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <p className="ms2-class-sel-count"><strong>{selected.size}</strong> selected</p>

          <div className="ms2-class-list ms2-subj-list">
            {loadingAll ? (
              <div className="ms2-class-list-empty"><Loader size={16} className="ms2-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="ms2-class-list-empty">No subjects yet — create one below</div>
            ) : (
              filtered.map((s) => {
                const isSel = selected.has(s.id);
                return (
                  <label key={s.id} className={`ms2-class-row${isSel ? " ms2-class-row--selected" : ""}`} onClick={() => toggle(s.id)}>
                    <span className="ms2-class-check-icon">{isSel && <Check size={11} strokeWidth={3} />}</span>
                    <div className="ms2-subj-info">
                      <span className="ms2-class-row-name">{s.name}</span>
                      {s.description && <span className="ms2-subj-desc">{s.description}</span>}
                    </div>
                    {assignedSubjectIds.includes(s.id) && (
                      <span className="ms2-class-assigned-badge" style={{ marginLeft: "auto" }}>Assigned</span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Create multiple new subjects */}
        <div className="ms2-class-new-section ms2-subj-new-section">
          <div className="ms2-subj-new-header">
            <p className="ms2-class-new-label">Create new subjects</p>
            <button className="ms2-subj-add-row-btn" onClick={addRow} type="button">
              <Plus size={12} /> Add another
            </button>
          </div>

          <div className="ms2-subj-rows">
            {rows.map((row, idx) => (
              <div key={row.uid} className="ms2-subj-new-row">
                <span className="ms2-subj-row-num">{idx + 1}</span>
                <input
                  placeholder="Subject name *"
                  value={row.name}
                  onChange={(e) => updateRow(row.uid, "name", e.target.value)}
                />
                <input
                  placeholder="Description (optional)"
                  value={row.desc}
                  onChange={(e) => updateRow(row.uid, "desc", e.target.value)}
                />
                {rows.length > 1 && (
                  <button className="ms2-subj-remove-btn" onClick={() => removeRow(row.uid)} title="Remove">
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            className="lms-btn-primary ms2-subj-create-btn"
            onClick={handleCreateAll}
            disabled={creatingAll || !validRows.length}
          >
            {creatingAll ? <Loader size={13} className="ms2-spin" /> : <Plus size={14} />}
            {validRows.length > 1 ? `Create ${validRows.length} Subjects` : "Create Subject"}
          </button>
        </div>

        {/* Footer */}
        <div className="ms2-class-modal-footer">
          <button className="lms-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="lms-btn-primary" onClick={handleSave} disabled={saving}>
            {saving && <Loader size={13} className="ms2-spin" />} Save Selection
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Chapter Picker Modal ── */
function ChapterPickerModal({ classSubjectId, assignedChapters, onClose, onSaved, onRefresh }) {
  const assignedIds = assignedChapters.map((c) => c.id);

  const [allChapters, setAllChapters] = useState([]);
  const [loadingAll, setLoadingAll]   = useState(true);
  const [selected, setSelected]       = useState(new Set(assignedIds));
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState("");
  const [creatingAll, setCreatingAll] = useState(false);
  const [rows, setRows]               = useState([mkRow()]);

  useEffect(() => {
    lmsGetChapters({ class_subject_id: classSubjectId })
      .then((r) => setAllChapters(r.data.data || []))
      .catch(() => toast.error("Failed to load chapters"))
      .finally(() => setLoadingAll(false));
  }, [classSubjectId]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const updateRow = (uid, field, val) =>
    setRows((prev) => prev.map((r) => r.uid === uid ? { ...r, [field]: val } : r));
  const addRow    = () => setRows((prev) => [...prev, mkRow()]);
  const removeRow = (uid) =>
    setRows((prev) => prev.length > 1 ? prev.filter((r) => r.uid !== uid) : [mkRow()]);

  const validRows = rows.filter((r) => r.name.trim());

  const handleCreateAll = async () => {
    if (!validRows.length) return;
    setCreatingAll(true);
    try {
      const results = await Promise.all(
        validRows.map((r, i) => lmsCreateChapter({
          class_subject_id: classSubjectId,
          name: r.name.trim(),
          description: r.desc.trim() || null,
          chapter_number: allChapters.length + i + 1,
        }))
      );
      const created = results.map((res) => res.data.data).filter(Boolean);
      setAllChapters((prev) => [...prev, ...created]);
      setSelected((prev) => new Set([...prev, ...created.map((c) => c.id)]));
      setRows([mkRow()]);
      toast.success(`${created.length} chapter${created.length > 1 ? "s" : ""} created`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to create chapters"); }
    finally { setCreatingAll(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaved({ selected: [...selected] });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const filtered = allChapters.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal ms2-subj-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-modal-header">
          <span>Manage Chapters</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="ms2-class-modal-body">
          <div className="ms2-class-search">
            <Search size={15} />
            <input placeholder="Search chapters..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <p className="ms2-class-sel-count"><strong>{selected.size}</strong> selected</p>

          <div className="ms2-class-list ms2-subj-list">
            {loadingAll ? (
              <div className="ms2-class-list-empty"><Loader size={16} className="ms2-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="ms2-class-list-empty">No chapters yet — create one below</div>
            ) : (
              filtered.map((c) => {
                const isSel = selected.has(c.id);
                return (
                  <label key={c.id} className={`ms2-class-row${isSel ? " ms2-class-row--selected" : ""}`} onClick={() => toggle(c.id)}>
                    <span className="ms2-class-check-icon">{isSel && <Check size={11} strokeWidth={3} />}</span>
                    <div className="ms2-subj-info">
                      <span className="ms2-class-row-name">
                        {c.chapter_number ? `Ch ${c.chapter_number} — ` : ""}{c.name}
                      </span>
                      {c.description && <span className="ms2-subj-desc">{c.description}</span>}
                    </div>
                    {assignedIds.includes(c.id) && (
                      <span className="ms2-class-assigned-badge" style={{ marginLeft: "auto" }}>Added</span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Create multiple chapters */}
        <div className="ms2-class-new-section ms2-subj-new-section">
          <div className="ms2-subj-new-header">
            <p className="ms2-class-new-label">Create new chapters</p>
            <button className="ms2-subj-add-row-btn" onClick={addRow} type="button">
              <Plus size={12} /> Add another
            </button>
          </div>

          <div className="ms2-subj-rows">
            {rows.map((row, idx) => (
              <div key={row.uid} className="ms2-subj-new-row">
                <span className="ms2-subj-row-num">{allChapters.length + idx + 1}</span>
                <input
                  placeholder="Chapter name *"
                  value={row.name}
                  onChange={(e) => updateRow(row.uid, "name", e.target.value)}
                />
                <input
                  placeholder="Description (optional)"
                  value={row.desc}
                  onChange={(e) => updateRow(row.uid, "desc", e.target.value)}
                />
                {rows.length > 1 && (
                  <button className="ms2-subj-remove-btn" onClick={() => removeRow(row.uid)} title="Remove">
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            className="lms-btn-primary ms2-subj-create-btn"
            onClick={handleCreateAll}
            disabled={creatingAll || !validRows.length}
          >
            {creatingAll ? <Loader size={13} className="ms2-spin" /> : <Plus size={14} />}
            {validRows.length > 1 ? `Create ${validRows.length} Chapters` : "Create Chapter"}
          </button>
        </div>

        <div className="ms2-class-modal-footer">
          <button className="lms-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="lms-btn-primary" onClick={handleSave} disabled={saving}>
            {saving && <Loader size={13} className="ms2-spin" />} Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Topic Picker Modal ── */
function TopicPickerModal({ classSubjectId, chapterId, assignedTopics, onClose, onRefresh }) {
  const assignedIds = assignedTopics.map((t) => t.id);

  const [allTopics, setAllTopics]     = useState([]);
  const [loadingAll, setLoadingAll]   = useState(true);
  const [search, setSearch]           = useState("");
  const [creatingAll, setCreatingAll] = useState(false);
  const [rows, setRows]               = useState([mkRow()]);

  useEffect(() => {
    lmsGetTopics({ class_subject_id: classSubjectId, chapter_id: chapterId })
      .then((r) => setAllTopics(r.data.data || []))
      .catch(() => toast.error("Failed to load topics"))
      .finally(() => setLoadingAll(false));
  }, [classSubjectId, chapterId]);

  const updateRow = (uid, field, val) =>
    setRows((prev) => prev.map((r) => r.uid === uid ? { ...r, [field]: val } : r));
  const addRow    = () => setRows((prev) => [...prev, { ...mkRow(), duration: "" }]);
  const removeRow = (uid) =>
    setRows((prev) => prev.length > 1 ? prev.filter((r) => r.uid !== uid) : [mkRow()]);

  const validRows = rows.filter((r) => r.name.trim());

  const handleCreateAll = async () => {
    if (!validRows.length) return;
    setCreatingAll(true);
    try {
      const results = await Promise.all(
        validRows.map((r, i) => lmsCreateTopic({
          class_subject_id: classSubjectId,
          chapter_id: chapterId,
          name: r.name.trim(),
          description: r.desc.trim() || null,
          duration_minutes: r.duration ? parseInt(r.duration) : null,
          topic_number: allTopics.length + i + 1,
        }))
      );
      const created = results.map((res) => res.data.data).filter(Boolean);
      setAllTopics((prev) => [...prev, ...created]);
      setRows([mkRow()]);
      toast.success(`${created.length} topic${created.length > 1 ? "s" : ""} created`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to create topics"); }
    finally { setCreatingAll(false); }
  };

  const filtered = allTopics.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal ms2-subj-modal ms2-topic-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-modal-header">
          <span>Manage Topics</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="ms2-class-modal-body">
          <div className="ms2-class-search">
            <Search size={15} />
            <input placeholder="Search topics..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <p className="ms2-class-sel-count"><strong>{allTopics.length}</strong> topic{allTopics.length !== 1 ? "s" : ""} in this chapter</p>

          <div className="ms2-class-list ms2-subj-list">
            {loadingAll ? (
              <div className="ms2-class-list-empty"><Loader size={16} className="ms2-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="ms2-class-list-empty">No topics yet — create one below</div>
            ) : (
              filtered.map((t) => (
                <div key={t.id} className="ms2-class-row">
                  <div className="ms2-subj-info">
                    <span className="ms2-class-row-name">
                      {t.topic_number ? `Topic ${t.topic_number}: ` : ""}{t.name}
                    </span>
                    {t.description && <span className="ms2-subj-desc">{t.description}</span>}
                  </div>
                  {t.duration_minutes && (
                    <span className="ms2-topic-duration">{t.duration_minutes} min</span>
                  )}
                  {assignedIds.includes(t.id) && (
                    <span className="ms2-class-assigned-badge" style={{ marginLeft: "auto" }}>Added</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Create multiple topics */}
        <div className="ms2-class-new-section ms2-subj-new-section">
          <div className="ms2-subj-new-header">
            <p className="ms2-class-new-label">Create new topics</p>
            <button className="ms2-subj-add-row-btn" onClick={addRow} type="button">
              <Plus size={12} /> Add another
            </button>
          </div>

          <div className="ms2-subj-rows">
            {rows.map((row, idx) => (
              <div key={row.uid} className="ms2-subj-new-row ms2-topic-new-row">
                <span className="ms2-subj-row-num">{allTopics.length + idx + 1}</span>
                <input
                  placeholder="Topic name *"
                  value={row.name}
                  onChange={(e) => updateRow(row.uid, "name", e.target.value)}
                />
                <input
                  className="ms2-topic-duration-input"
                  placeholder="Duration (min)"
                  type="number"
                  min="1"
                  value={row.duration || ""}
                  onChange={(e) => updateRow(row.uid, "duration", e.target.value)}
                />
                {rows.length > 1 && (
                  <button className="ms2-subj-remove-btn" onClick={() => removeRow(row.uid)} title="Remove">
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            className="lms-btn-primary ms2-subj-create-btn"
            onClick={handleCreateAll}
            disabled={creatingAll || !validRows.length}
          >
            {creatingAll ? <Loader size={13} className="ms2-spin" /> : <Plus size={14} />}
            {validRows.length > 1 ? `Create ${validRows.length} Topics` : "Create Topic"}
          </button>
        </div>

        <div className="ms2-class-modal-footer">
          <button className="lms-btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Modal ── */
function EditModal({ title, fields, initial, onSave, onClose, saving }) {
  const [values, setValues] = useState(initial || {});
  const set = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));
  const canSave = fields.filter((f) => f.required).every((f) => String(values[f.key] || "").trim());

  const handleSave = async () => {
    if (!canSave) return;
    const payload = {};
    fields.forEach((f) => { payload[f.key] = f.type === "number" ? (values[f.key] ? parseInt(values[f.key]) : null) : (String(values[f.key] || "").trim() || null); });
    await onSave(payload);
    onClose();
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal ms2-video-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-modal-header">
          <span>Edit {title}</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="ms2-video-modal-body">
          {fields.map((f, i) => (
            <React.Fragment key={f.key}>
              <label className="ms2-video-label" style={i > 0 ? { marginTop: 14 } : {}}>
                {f.label}{f.required && <span style={{ color: "#dc2626" }}> *</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea className="ms2-video-input ms2-video-textarea" placeholder={f.placeholder}
                  value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={3} />
              ) : (
                <input className={`ms2-video-input${f.type === "number" ? " ms2-video-duration-input" : ""}`}
                  type={f.type || "text"} placeholder={f.placeholder} autoFocus={i === 0}
                  value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="ms2-class-modal-footer">
          <button className="lms-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="lms-btn-primary" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? <Loader size={13} className="ms2-spin" /> : <Pencil size={14} />} Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Panel item ── */
function PanelItem({ name, meta, selected, onClick, onDelete, onEdit }) {
  const [hover, setHover] = useState(false);
  return (
    <div className={`ms2-item ${selected ? "ms2-item--active" : ""}`}
      onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="ms2-item-body">
        <div className="ms2-item-name">{name}</div>
        {meta && <div className="ms2-item-meta">{meta}</div>}
      </div>
      <div className="ms2-item-actions">
        {hover && onEdit && (
          <button className="ms2-edit-btn" onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit">
            <Pencil size={12} />
          </button>
        )}
        {hover && onDelete && (
          <button className="ms2-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete">
            <Trash2 size={13} />
          </button>
        )}
        <ChevronRight size={15} className="ms2-item-chevron" />
      </div>
    </div>
  );
}

/* ── Column panel ── */
function Panel({ num, icon: Icon, title, items, selectedId, onSelect, onDelete, onEdit, onAddClick, disabled, emptyText }) {
  return (
    <div className={`ms2-panel ${disabled ? "ms2-panel--disabled" : ""}`}>
      <div className="ms2-panel-header">
        <div className="ms2-panel-title"><Icon size={15} strokeWidth={2} /><span>{num}. {title}</span></div>
        <button className="ms2-panel-addbtn" onClick={onAddClick} disabled={disabled} title={`Add ${title}`}>
          <Plus size={16} strokeWidth={2.5} />
        </button>
      </div>
      <div className="ms2-panel-list">
        {items.length === 0
          ? <div className="ms2-empty">{disabled ? "Select previous column first" : emptyText}</div>
          : items.map((item) => (
            <PanelItem key={item.id} name={item.name} meta={item.meta}
              selected={selectedId === item.id}
              onClick={() => onSelect(item)}
              onDelete={onDelete ? () => onDelete(item.id) : null}
              onEdit={onEdit ? () => onEdit(item) : null}
            />
          ))
        }
      </div>
    </div>
  );
}

const DOC_TYPES = [
  { value: "study_material", label: "Study Material" },
  { value: "homework",       label: "Homework" },
  { value: "worksheet",      label: "Worksheet" },
];

const mkFileRow  = () => ({ uid: Date.now() + Math.random(), file: null, title: "", desc: "", docType: "study_material" });
const mkVideoRow = () => ({ uid: Date.now() + Math.random(), title: "", desc: "", url: "", duration: "", docType: "study_material" });

/* ── Add Study Material Modal (File + Video tabs, multi-row) ── */
function AddMaterialModal({ initialFile, onClose, onUpload, uploading, onAddVideoLink, addingLink }) {
  const [tab, setTab] = useState("file");
  const [fileRows,  setFileRows]  = useState(() => {
    const r = mkFileRow();
    if (initialFile) { r.file = initialFile; r.title = initialFile.name.replace(/\.[^.]+$/, ""); }
    return [r];
  });
  const [videoRows, setVideoRows] = useState([mkVideoRow()]);
  const [submitting, setSubmitting] = useState(false);

  const fileInputRefs = useRef({});

  /* file rows helpers */
  const updateFileRow = (uid, field, val) =>
    setFileRows((prev) => prev.map((r) => r.uid === uid ? { ...r, [field]: val } : r));
  const addFileRow    = () => setFileRows((prev) => [...prev, mkFileRow()]);
  const removeFileRow = (uid) => setFileRows((prev) => prev.length > 1 ? prev.filter((r) => r.uid !== uid) : prev);
  const pickFileForRow = (uid, files) => {
    if (files && files[0]) {
      const f = files[0];
      setFileRows((prev) => prev.map((r) => r.uid === uid ? { ...r, file: f, title: r.title || f.name.replace(/\.[^.]+$/, "") } : r));
    }
  };

  /* video rows helpers */
  const updateVideoRow = (uid, field, val) =>
    setVideoRows((prev) => prev.map((r) => r.uid === uid ? { ...r, [field]: val } : r));
  const addVideoRow    = () => setVideoRows((prev) => [...prev, mkVideoRow()]);
  const removeVideoRow = (uid) => setVideoRows((prev) => prev.length > 1 ? prev.filter((r) => r.uid !== uid) : prev);

  const validFileRows  = fileRows.filter((r) => r.file && r.title.trim());
  const validVideoRows = videoRows.filter((r) => r.title.trim() && r.url.trim());
  const canSubmit = tab === "file" ? validFileRows.length > 0 : validVideoRows.length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (tab === "file") {
        await Promise.all(validFileRows.map((r) => onUpload(r.file, r.title.trim(), r.desc.trim() || null, r.docType)));
        toast.success(validFileRows.length > 1 ? `${validFileRows.length} files uploaded successfully` : `"${validFileRows[0].title.trim() || validFileRows[0].file?.name}" uploaded`);
      } else {
        await Promise.all(validVideoRows.map((r) => onAddVideoLink({
          title: r.title.trim(), url: r.url.trim(),
          description: r.desc.trim() || null,
          duration: r.duration ? parseInt(r.duration) : null,
          document_type: r.docType,
        })));
        toast.success(validVideoRows.length > 1 ? `${validVideoRows.length} video links added` : `Video link "${validVideoRows[0].title.trim()}" added`);
      }
      onClose();
    } finally { setSubmitting(false); }
  };

  const isLoading = submitting || (tab === "file" ? uploading : addingLink);
  const submitLabel = tab === "file"
    ? (validFileRows.length > 1 ? `Upload ${validFileRows.length} Content Files` : "Upload Content File")
    : (validVideoRows.length > 1 ? `Add ${validVideoRows.length} Videos` : "Add Video");

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal ms2-mat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-modal-header">
          <span>Add Study Material</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Tabs */}
        <div className="ms2-mat-tabs">
          <button className={`ms2-mat-tab${tab === "file" ? " ms2-mat-tab--active" : ""}`} onClick={() => setTab("file")}>
            <Upload size={13} /> Content File
          </button>
          <button className={`ms2-mat-tab${tab === "video" ? " ms2-mat-tab--active" : ""}`} onClick={() => setTab("video")}>
            <Play size={13} /> Video Link
          </button>
        </div>

        <div className="ms2-mat-modal-body">
          {/* ── FILE TAB ── */}
          {tab === "file" && (
            <>
              <div className="ms2-subj-new-header">
                <p className="ms2-class-new-label">Content Files</p>
                <button className="ms2-subj-add-row-btn" onClick={addFileRow} type="button">
                  <Plus size={12} /> Add another
                </button>
              </div>
              <div className="ms2-subj-rows">
                {fileRows.map((row) => (
                  <div key={row.uid} className="ms2-mat-file-block">
                    {/* Row 1: file picker + title + type + remove */}
                    <div className="ms2-mat-file-row">
                      {row.file ? (
                        <div className="ms2-mat-file-chip">
                          <FileText size={13} color="#4f46e5" />
                          <span>{row.file.name}</span>
                          <span className="ms2-upload-file-size">{(row.file.size / (1024*1024)).toFixed(1)}MB</span>
                          <button className="ms2-subj-remove-btn" onClick={() => updateFileRow(row.uid, "file", null)}><X size={11} /></button>
                        </div>
                      ) : (
                        <button className="ms2-mat-pick-btn" onClick={() => fileInputRefs.current[row.uid]?.click()}>
                          <Upload size={13} /> Choose file
                          <input type="file" style={{ display: "none" }} ref={(el) => fileInputRefs.current[row.uid] = el}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.webm,.ogg,.mov,.avi,.mkv,.jpg,.jpeg,.png,.gif,.webp"
                            onChange={(e) => pickFileForRow(row.uid, e.target.files)} />
                        </button>
                      )}
                      <input className="ms2-video-input ms2-mat-title-input" placeholder="Title *"
                        value={row.title} onChange={(e) => updateFileRow(row.uid, "title", e.target.value)} />
                      <select className="ms2-video-input ms2-mat-type-select"
                        value={row.docType} onChange={(e) => updateFileRow(row.uid, "docType", e.target.value)}>
                        {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      {fileRows.length > 1 && (
                        <button className="ms2-subj-remove-btn" onClick={() => removeFileRow(row.uid)}><X size={12} /></button>
                      )}
                    </div>
                    {/* Row 2: description textarea */}
                    <textarea className="ms2-video-input ms2-mat-desc-textarea"
                      placeholder="Description (optional)" rows={2}
                      value={row.desc} onChange={(e) => updateFileRow(row.uid, "desc", e.target.value)} />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── VIDEO TAB ── */}
          {tab === "video" && (
            <>
              <div className="ms2-subj-new-header">
                <p className="ms2-class-new-label">Video links to add</p>
                <button className="ms2-subj-add-row-btn" onClick={addVideoRow} type="button">
                  <Plus size={12} /> Add another
                </button>
              </div>
              <div className="ms2-subj-rows">
                {videoRows.map((row, idx) => (
                  <div key={row.uid} className="ms2-mat-file-block">
                    <div className="ms2-mat-video-header-row">
                      <span className="ms2-subj-row-num">{idx + 1}</span>
                      {videoRows.length > 1 && (
                        <button className="ms2-subj-remove-btn ms2-video-remove-btn" onClick={() => removeVideoRow(row.uid)}><X size={12} /></button>
                      )}
                    </div>
                    <div className="ms2-mat-video-2col ms2-video-row1">
                      <select className="ms2-mat-type-select ms2-video-input"
                        value={row.docType} onChange={(e) => updateVideoRow(row.uid, "docType", e.target.value)}>
                        {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <input className="ms2-video-input" placeholder="Title *"
                        value={row.title} onChange={(e) => updateVideoRow(row.uid, "title", e.target.value)} />
                    </div>
                    <div className="ms2-mat-video-2col ms2-video-row2">
                      <input className="ms2-video-input" placeholder="Video URL * (YouTube, Drive…)"
                        value={row.url} onChange={(e) => updateVideoRow(row.uid, "url", e.target.value)} />
                      <input className="ms2-video-input" placeholder="Min" type="number" min="1"
                        value={row.duration} onChange={(e) => updateVideoRow(row.uid, "duration", e.target.value)} />
                    </div>
                    <textarea className="ms2-mat-desc-textarea" placeholder="Description (optional)" rows={2}
                      value={row.desc} onChange={(e) => updateVideoRow(row.uid, "desc", e.target.value)} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="ms2-class-modal-footer">
          <button className="lms-btn-ghost" onClick={onClose} disabled={isLoading}>Cancel</button>
          <button className="lms-btn-primary" onClick={handleSubmit} disabled={isLoading || !canSubmit}>
            {isLoading ? <Loader size={13} className="ms2-spin" /> : tab === "file" ? <Upload size={14} /> : <Plus size={14} />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── File Upload Panel ── */
function FileUploadPanel({ targetName, materials, materialsLoading, onUpload, uploading, onAddVideoLink, addingLink, onDeleteMaterial, onEditMaterial }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver]       = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const pickFile = (files) => { if (files && files[0]) { setPendingFile(files[0]); setShowModal(true); } };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); };
  const closeModal = () => { setShowModal(false); setPendingFile(null); if (inputRef.current) inputRef.current.value = ""; };

  return (
    <>
    {showModal && (
      <AddMaterialModal
        initialFile={pendingFile}
        onClose={closeModal}
        onUpload={async (file, title, desc) => { await onUpload(file, title, desc); }}
        uploading={uploading}
        onAddVideoLink={onAddVideoLink}
        addingLink={addingLink}
      />
    )}
    <div className="ms2-panel ms2-upload-panel">
      <div className="ms2-panel-header">
        <div className="ms2-panel-title"><CloudUpload size={15} strokeWidth={2} /><span>5. Study Material</span></div>
        <button className="ms2-panel-addbtn" title={targetName ? "Add Material" : "Select a topic first"}
          onClick={() => setShowModal(true)} disabled={!targetName}>
          <Plus size={16} strokeWidth={2.5} />
        </button>
      </div>
      <div className="ms2-upload-body">
        {/* Show target topic label only when a topic is selected */}
        {targetName && (
          <div className="ms2-target-topic">
            <div className="ms2-target-label">TARGET TOPIC:</div>
            <div className="ms2-target-name">{targetName}</div>
          </div>
        )}

        <input ref={inputRef} type="file" style={{ display: "none" }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.webm,.ogg,.mov,.avi,.mkv,.jpg,.jpeg,.png,.gif,.webp"
          onChange={(e) => pickFile(e.target.files)} />

        {/* Materials list — always visible */}
        <div className="ms2-materials-section">
          <div className="ms2-materials-label">STUDY MATERIALS:</div>
          {materialsLoading ? (
            <div className="ms2-empty"><Loader size={16} className="ms2-spin" /></div>
          ) : materials.length === 0 ? (
            <div className="ms2-empty" style={{ padding: "12px 0" }}>No materials yet</div>
          ) : (
            <div className="ms2-materials-list">
              {materials.map((m) => {
                const badge = getBadge(m.material_type, m.file_name);
                return (
                  <div key={m.id} className="ms2-material-row">
                    <span className="ms2-type-badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                    <div className="ms2-material-info">
                      <div className="ms2-material-name">{m.title || m.file_name}</div>
                      <div className="ms2-material-meta">
                        {fmtSize(m.file_size)}{m.file_size ? " • " : ""}{fmtDate(m.created_at)}
                      </div>
                    </div>
                    <div className="ms2-material-actions">
                      {m.file_url && (
                        <a href={m.file_url} target="_blank" rel="noreferrer" className="ms2-mat-action-btn"
                          title={m.material_type === "video" ? "Play" : "View"}>
                          {m.material_type === "video" ? <Play size={14} /> : <Eye size={14} />}
                        </a>
                      )}
                      <button className="ms2-mat-action-btn" title="Edit" onClick={() => onEditMaterial(m)}>
                        <Pencil size={13} />
                      </button>
                      <button className="ms2-mat-action-btn ms2-mat-delete" title="Delete" onClick={() => onDeleteMaterial(m.id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

/* ══════════════════════════════════════════
   MASTER SETUP — main page
══════════════════════════════════════════ */
export default function MasterSetup() {
  const dispatch = useDispatch();
  const {
    schoolClasses, classSubjects, chapters, topics, materials,
    materialsLoading, selectedClassId, selectedClassSubjectId, selectedChapterId, selectedTopicId,
  } = useSelector((s) => s.lms);

  const schoolId = getSchoolId();

  const [modal, setModal]                           = useState(null);
  const [showClassPicker, setShowClassPicker]       = useState(false);
  const [showSubjectPicker, setShowSubjectPicker]   = useState(false);
  const [showChapterPicker, setShowChapterPicker]   = useState(false);
  const [showTopicPicker, setShowTopicPicker]       = useState(false);
  const [addingSubject, setAddingSubject]           = useState(false);
  const [confirm, setConfirm]                     = useState(null); // { message, onConfirm } | null
  const [confirmLoading, setConfirmLoading]       = useState(false);
  const [addingChapter, setAddingChapter]     = useState(false);
  const [addingTopic, setAddingTopic]         = useState(false);
  const [uploading, setUploading]             = useState(false);
  const [addingLink, setAddingLink]           = useState(false);
  const [editTarget, setEditTarget]           = useState(null); // { type, item }
  const [editSaving, setEditSaving]           = useState(false);

  // Fetch ALL data on mount — filter client-side based on selection
  useEffect(() => {
    dispatch(fetchLmsSchoolClasses(schoolId));
    dispatch(fetchLmsClassSubjects({ school_id: schoolId }));
    dispatch(fetchLmsChapters({ school_id: schoolId }));
    dispatch(fetchLmsTopics({ school_id: schoolId }));
    dispatch(fetchLmsMaterials({ school_id: schoolId }));
  }, [dispatch, schoolId]);

  /* ── derived ── */
  const selectedSchoolClass = schoolClasses.find((sc) => sc.class_id === selectedClassId);
  const selectedSubject     = classSubjects.find((cs) => cs.id === selectedClassSubjectId);
  const selectedChapter     = chapters.find((ch) => ch.id === selectedChapterId);
  const selectedTopic       = topics.find((t)  => t.id  === selectedTopicId);

  const activeStep = selectedTopicId ? 4 : selectedChapterId ? 3 : selectedClassSubjectId ? 2 : selectedClassId ? 1 : 0;

  const breadcrumb = [
    selectedSchoolClass?.class_name,
    selectedSubject?.subject_name,
    selectedChapter?.name,
    selectedTopic?.name,
  ].filter(Boolean).join(" > ") || null;

  /* IDs of global classes already assigned to this school */
  const assignedClassIds = schoolClasses.map((sc) => sc.class_id);

  /* ── column 1 items — school-specific classes ── */
  const classItems = schoolClasses.map((sc) => ({
    id: sc.class_id, scId: sc.id, name: sc.class_name, meta: null,
    globalId: sc.class_id,
  }));

  // Column 2: all subjects, filtered by selected class
  const filteredSubjects = selectedClassId
    ? classSubjects.filter((cs) => cs.class_id === selectedClassId)
    : classSubjects;
  const subjectItems = filteredSubjects.map((cs) => ({
    id: cs.id, name: cs.subject_name,
    meta: selectedClassId ? null : cs.class_name, // show class name only when unfiltered
    subjectId: cs.subject_id, rawName: cs.subject_name, rawDesc: cs.description,
  }));

  // Column 3: all chapters, filtered by selected subject
  const filteredChapters = selectedClassSubjectId
    ? chapters.filter((ch) => ch.class_subject_id === selectedClassSubjectId)
    : chapters;
  const chapterItems = filteredChapters.map((ch) => ({
    id: ch.id,
    name: ch.chapter_number ? `Ch ${ch.chapter_number} — ${ch.name}` : ch.name,
    meta: selectedClassSubjectId ? null : (ch.subject_name || null),
    rawName: ch.name, rawDesc: ch.description, rawNum: ch.chapter_number,
  }));

  // Column 4: all topics, filtered by selected chapter
  const filteredTopics = selectedChapterId
    ? topics.filter((t) => t.chapter_id === selectedChapterId)
    : selectedClassSubjectId
      ? topics.filter((t) => t.class_subject_id === selectedClassSubjectId)
      : topics;
  const topicItems = filteredTopics.map((t) => ({
    id: t.id,
    name: t.topic_number ? `Topic ${t.topic_number}: ${t.name}` : t.name,
    meta: selectedChapterId ? null : (t.chapter_name || null),
    rawName: t.name, rawDesc: t.description, rawDuration: t.duration_minutes, rawNum: t.topic_number,
  }));

  // Column 5: materials filtered by selected topic (show all if none selected)
  const filteredMaterials = selectedTopicId
    ? materials.filter((m) => m.topic_id === selectedTopicId)
    : selectedChapterId
      ? materials.filter((m) => m.chapter_id === selectedChapterId)
      : selectedClassSubjectId
        ? materials.filter((m) => m.class_subject_id === selectedClassSubjectId)
        : materials;

  /* ── edit config per entity type ── */
  const EDIT_CONFIGS = {
    class:    { title: "Class",   fields: [{ key: "name", label: "Class Name", placeholder: "e.g. Class 1", required: true }] },
    subject:  { title: "Subject", fields: [{ key: "name", label: "Subject Name", placeholder: "e.g. Mathematics", required: true }, { key: "description", label: "Description", placeholder: "Brief description", type: "textarea" }] },
    chapter:  { title: "Chapter", fields: [{ key: "chapter_number", label: "Chapter Number", placeholder: "e.g. 1", type: "number" }, { key: "name", label: "Chapter Name", placeholder: "e.g. Introduction", required: true }, { key: "description", label: "Description", placeholder: "Brief description", type: "textarea" }] },
    topic:    { title: "Topic",   fields: [{ key: "topic_number", label: "Topic Number", placeholder: "e.g. 1", type: "number" }, { key: "name", label: "Topic Name", placeholder: "e.g. What is...", required: true }, { key: "description", label: "Description", placeholder: "Brief description", type: "textarea" }, { key: "duration_minutes", label: "Duration (minutes)", placeholder: "e.g. 15", type: "number" }] },
    material: { title: "Material", fields: [{ key: "title", label: "Title", placeholder: "Material title", required: true }, { key: "description", label: "Description", placeholder: "Brief description", type: "textarea" }] },
  };

  /* ── class picker save ── */
  const handleClassPickerSave = async ({ added, removed }) => {
    // added: array of class_ids to assign
    // removed: array of class_ids to un-assign — find the sc.id
    try {
      if (added.length > 0) {
        await Promise.all(added.map((class_id) => lmsCreateSchoolClass({ school_id: schoolId, class_id })));
        toast.success(`${added.length} class${added.length > 1 ? "es" : ""} added`);
      }
      if (removed.length > 0) {
        const toRemoveSc = schoolClasses.filter((sc) => removed.includes(sc.class_id));
        await Promise.all(toRemoveSc.map((sc) => lmsDeleteSchoolClass(sc.id)));
        toast.success(`${removed.length} class${removed.length > 1 ? "es" : ""} removed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update classes");
    } finally {
      dispatch(fetchLmsSchoolClasses(schoolId));
      dispatch(fetchLmsClassSubjects({ school_id: schoolId }));
    }
  };

  /* ── subject picker save ── */
  const handleSubjectPickerSave = async ({ added, removed }) => {
    try {
      if (added.length > 0) {
        await Promise.all(added.map((subject_id) =>
          lmsCreateClassSubject({ class_id: selectedClassId, subject_id, school_id: schoolId })
        ));
        toast.success(`${added.length} subject${added.length > 1 ? "s" : ""} added`);
      }
      if (removed.length > 0) {
        const toRemoveCs = classSubjects.filter((cs) => removed.includes(cs.subject_id));
        await Promise.all(toRemoveCs.map((cs) => lmsDeleteClassSubject(cs.id)));
        toast.success(`${removed.length} subject${removed.length > 1 ? "s" : ""} removed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update subjects");
      throw err;
    } finally {
      dispatch(fetchLmsClassSubjects({ school_id: schoolId }));
    }
  };

  /* ── confirm helper ── */
  const askConfirm = (message, onConfirm) => setConfirm({ message, onConfirm });

  const runConfirmed = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try { await confirm.onConfirm(); }
    finally { setConfirmLoading(false); setConfirm(null); }
  };

  /* ── remove class from school ── */
  const handleRemoveSchoolClass = (classId) => {
    const sc = schoolClasses.find((s) => s.class_id === classId);
    if (!sc) return;
    const name = sc.class_name || "this class";
    askConfirm(`Are you sure you want to delete the class "${name}" from your school? This action cannot be undone.`, async () => {
      await lmsDeleteSchoolClass(sc.id);
      toast.success("Class removed from school");
      dispatch(fetchLmsSchoolClasses(schoolId));
      if (selectedClassId === classId) dispatch(setSelectedClass(null));
    });
  };

  /* ── subject handlers ── */
  const handleAddSubject = async (name, done) => {
    if (!selectedClassId) return;
    setAddingSubject(true);
    try {
      const subRes = await lmsCreateSubject({ school_id: schoolId, name });
      const subjectId = subRes.data.data.id;
      const csRes = await lmsCreateClassSubject({ class_id: selectedClassId, subject_id: subjectId, school_id: schoolId });
      dispatch(addSubjectOptimistic(csRes.data.data));
      toast.success(`Subject "${name}" added`);
      done();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to add subject"); }
    finally { setAddingSubject(false); dispatch(fetchLmsClassSubjects({ school_id: schoolId })); }
  };

  const handleDeleteSubject = (id) => {
    const cs = classSubjects.find((s) => s.id === id);
    const name = cs?.subject_name || "this subject";
    askConfirm(`Are you sure you want to delete the subject "${name}" from this class? This action cannot be undone.`, async () => {
      await lmsDeleteClassSubject(id);
      toast.success("Subject removed");
      dispatch(fetchLmsClassSubjects({ school_id: schoolId }));
      if (selectedClassSubjectId === id) dispatch(setSelectedClassSubject(null));
    });
  };

  /* ── chapter/topic handlers ── */
  const handleAddChapter = async (name, done) => {
    if (!selectedClassSubjectId) return;
    setAddingChapter(true);
    try {
      const res = await lmsCreateChapter({
        class_subject_id: selectedClassSubjectId,
        name,
        chapter_number: chapters.length + 1,
      });
      dispatch(addChapterOptimistic(res.data.data));
      toast.success(`Topic "${name}" added`);
      done();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to add topic"); }
    finally { setAddingChapter(false); dispatch(fetchLmsChapters({ school_id: schoolId })); }
  };

  const handleDeleteChapter = (id) => {
    const ch = chapters.find((c) => c.id === id);
    const name = ch?.name || "this chapter";
    askConfirm(`Are you sure you want to delete the chapter "${name}"? All associated topics will also be permanently deleted.`, async () => {
      await lmsDeleteChapter(id);
      toast.success("Chapter deleted");
      dispatch(fetchLmsChapters({ school_id: schoolId }));
      if (selectedChapterId === id) dispatch(setSelectedChapter(null));
    });
  };

  /* ── add topic ── */
  const handleAddTopic = async (name, done) => {
    if (!selectedClassSubjectId) return;
    setAddingTopic(true);
    try {
      const res = await lmsCreateTopic({ class_subject_id: selectedClassSubjectId, chapter_id: selectedChapterId, name });
      dispatch(addTopicOptimistic(res.data.data || { id: Date.now(), name }));
      toast.success("Topic added");
      done?.();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to add topic"); }
    finally { setAddingTopic(false); }
  };

  const handleDeleteTopic = (id) => {
    const t = topics.find((tp) => tp.id === id);
    const name = t?.name || "this topic";
    askConfirm(`Are you sure you want to delete the topic "${name}"? All associated study materials will also be permanently deleted.`, async () => {
      await lmsDeleteTopic(id);
      toast.success("Topic deleted");
      dispatch(fetchLmsTopics({ school_id: schoolId }));
      if (selectedTopicId === id) dispatch(setSelectedTopic(null));
    });
  };

  /* ── edit handlers ── */
  const handleEditSave = async (payload) => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const { type, item } = editTarget;
      if (type === "class") {
        await lmsUpdateClass(item.globalId, { name: payload.name });
        dispatch(fetchLmsSchoolClasses(schoolId));
      } else if (type === "subject") {
        await lmsUpdateSubject(item.subjectId, { name: payload.name, description: payload.description });
        dispatch(fetchLmsClassSubjects({ school_id: schoolId }));
      } else if (type === "chapter") {
        await lmsUpdateChapter(item.id, { name: payload.name, description: payload.description, chapter_number: payload.chapter_number });
        dispatch(fetchLmsChapters({ school_id: schoolId }));
      } else if (type === "topic") {
        await lmsUpdateTopic(item.id, { name: payload.name, description: payload.description, duration_minutes: payload.duration_minutes, topic_number: payload.topic_number });
        dispatch(fetchLmsTopics({ school_id: schoolId }));
      } else if (type === "material") {
        await lmsUpdateMaterial(item.id, { title: payload.title, description: payload.description });
        dispatch(fetchLmsMaterials({ school_id: schoolId }));
      }
      toast.success("Updated successfully");
    } catch (err) { toast.error(err.response?.data?.message || "Update failed"); throw err; }
    finally { setEditSaving(false); }
  };

  /* ── file upload ── */
  const handleUpload = async (file, title, description, document_type) => {
    if (!selectedClassSubjectId) { toast.warn("Select a subject first"); return; }
    if (!selectedTopicId) { toast.warn("Select a topic first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("school_id", schoolId);
      fd.append("class_subject_id", String(selectedClassSubjectId));
      fd.append("chapter_id", selectedChapterId ? String(selectedChapterId) : "");
      fd.append("topic_id", selectedTopicId ? String(selectedTopicId) : "");
      fd.append("title", title || file.name.replace(/\.[^.]+$/, ""));
      if (description) fd.append("description", description);
      fd.append("document_type", document_type || "study_material");
      await lmsUploadMaterial(fd);
      dispatch(fetchLmsMaterials({ school_id: schoolId }));
    } catch (err) { toast.error(err.response?.data?.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const handleAddVideoLink = async ({ title, url, description, duration, document_type }) => {
    if (!selectedClassSubjectId) { toast.warn("Select a subject first"); return; }
    if (!selectedTopicId) { toast.warn("Select a topic first"); return; }
    setAddingLink(true);
    try {
      await lmsAddVideoUrl({
        school_id: schoolId,
        class_subject_id: selectedClassSubjectId,
        chapter_id: selectedChapterId,
        topic_id: selectedTopicId,
        title,
        file_url: url,
        material_type: "video",
        description: description || null,
        duration_minutes: duration || null,
        document_type: document_type || "study_material",
      });
      dispatch(fetchLmsMaterials({ school_id: schoolId }));
    } catch (err) { toast.error(err.response?.data?.message || "Failed to add video link"); }
    finally { setAddingLink(false); }
  };

  const handleDeleteMaterial = (id) => {
    const m = materials.find((mat) => mat.id === id);
    const name = m?.title || m?.file_name || "this material";
    askConfirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`, async () => {
      await lmsDeleteMaterial(id);
      toast.success("Material deleted");
      dispatch(fetchLmsMaterials({ school_id: schoolId }));
    });
  };

  const MODAL_CONFIG = {
    subject: { title: "Add New Subject", placeholder: "e.g. Mathematics",          onAdd: handleAddSubject, loading: addingSubject },
    chapter: { title: "Add New Chapter", placeholder: "e.g. Chapter 1 — Algebra",  onAdd: handleAddChapter, loading: addingChapter },
    topic:   { title: "Add New Topic",   placeholder: "e.g. Topic 1: Introduction", onAdd: handleAddTopic,   loading: addingTopic   },
  };

  return (
    <div className="ms2-root">
      {/* Page header */}
      <div className="lms-page-header">
        <div>
          <h1 className="lms-page-title">Master Data Management</h1>
          <p className="lms-page-subtitle">4-Step Academic Setup: Class → Subject → Chapter → Topic</p>
        </div>
        <div className="ms2-header-actions">
          <button className="ms2-hdr-btn ms2-hdr-btn--primary" onClick={() => setShowClassPicker(true)}>
            <Layers size={14} /> + Class
          </button>
        </div>
      </div>

      {/* Stepper */}
      <Stepper activeStep={activeStep} breadcrumb={breadcrumb} />

      {/* 5 columns */}
      <div className="ms2-panels">
        {/* Column 1 — Classes */}
        <Panel
          num={1} icon={Layers} title="Classes"
          items={classItems} selectedId={selectedClassId}
          onSelect={(item) => dispatch(setSelectedClass(item.id))}
          onDelete={handleRemoveSchoolClass}
          onEdit={(item) => setEditTarget({ type: "class", item, initial: { name: item.name } })}
          onAddClick={() => setShowClassPicker(true)}
          disabled={false}
          emptyText="No classes assigned. Click + to add."
        />

        {/* Column 2 — Subjects */}
        <Panel
          num={2} icon={BookOpen} title="Subjects"
          items={subjectItems} selectedId={selectedClassSubjectId}
          onSelect={(item) => {
            dispatch(setSelectedClassSubject(item.id));
            // auto-select the class this subject belongs to if not already selected
            const cs = classSubjects.find((c) => c.id === item.id);
            if (cs && selectedClassId !== cs.class_id) dispatch(setSelectedClass(cs.class_id));
          }}
          onDelete={handleDeleteSubject}
          onEdit={(item) => setEditTarget({ type: "subject", item, initial: { name: item.rawName, description: item.rawDesc } })}
          onAddClick={() => { if (!selectedClassId) { toast.warn("Select a class first"); return; } setShowSubjectPicker(true); }}
          disabled={false} emptyText="No subjects yet. Select a class and click + to add."
        />

        {/* Column 3 — Chapters */}
        <Panel
          num={3} icon={FileText} title="Chapters"
          items={chapterItems} selectedId={selectedChapterId}
          onSelect={(item) => {
            const ch = chapters.find((c) => c.id === item.id);
            // auto-select subject first (it clears chapter+topic), then chapter
            if (ch && selectedClassSubjectId !== ch.class_subject_id) dispatch(setSelectedClassSubject(ch.class_subject_id));
            dispatch(setSelectedChapter(item.id));
          }}
          onDelete={handleDeleteChapter}
          onEdit={(item) => setEditTarget({ type: "chapter", item, initial: { chapter_number: item.rawNum, name: item.rawName, description: item.rawDesc } })}
          onAddClick={() => { if (!selectedClassSubjectId) { toast.warn("Select a subject first"); return; } setShowChapterPicker(true); }}
          disabled={false} emptyText="No chapters yet. Select a subject and click + to add."
        />

        {/* Column 4 — Topics */}
        <Panel
          num={4} icon={FileText} title="Topics"
          items={topicItems} selectedId={selectedTopicId}
          onSelect={(item) => {
            const t = topics.find((tp) => tp.id === item.id);
            if (t) {
              // auto-select subject + chapter (each clears downstream, so order matters)
              if (selectedClassSubjectId !== t.class_subject_id) dispatch(setSelectedClassSubject(t.class_subject_id));
              if (selectedChapterId !== t.chapter_id) dispatch(setSelectedChapter(t.chapter_id));
            }
            dispatch(setSelectedTopic(item.id));
          }}
          onDelete={handleDeleteTopic}
          onEdit={(item) => setEditTarget({ type: "topic", item, initial: { topic_number: item.rawNum, name: item.rawName, description: item.rawDesc, duration_minutes: item.rawDuration } })}
          onAddClick={() => { if (!selectedChapterId) { toast.warn("Select a chapter first"); return; } setShowTopicPicker(true); }}
          disabled={false} emptyText="No topics yet. Select a chapter and click + to add."
        />

      </div>

      {/* Class picker modal */}
      {showClassPicker && (
        <ClassPickerModal
          schoolId={schoolId}
          assignedClassIds={assignedClassIds}
          onClose={() => setShowClassPicker(false)}
          onSaved={handleClassPickerSave}
        />
      )}

      {/* Subject picker modal */}
      {showSubjectPicker && (
        <SubjectPickerModal
          schoolId={schoolId}
          classId={selectedClassId}
          assignedSubjects={filteredSubjects}
          onClose={() => setShowSubjectPicker(false)}
          onSaved={handleSubjectPickerSave}
          onRefresh={() => dispatch(fetchLmsClassSubjects({ school_id: schoolId }))}
        />
      )}

      {/* Chapter picker modal */}
      {showChapterPicker && (
        <ChapterPickerModal
          classSubjectId={selectedClassSubjectId}
          assignedChapters={chapters}
          onClose={() => setShowChapterPicker(false)}
          onSaved={async () => {}}
          onRefresh={() => dispatch(fetchLmsChapters({ class_subject_id: selectedClassSubjectId }))}
        />
      )}

      {/* Topic picker modal */}
      {showTopicPicker && (
        <TopicPickerModal
          classSubjectId={selectedClassSubjectId}
          chapterId={selectedChapterId}
          assignedTopics={topics}
          onClose={() => setShowTopicPicker(false)}
          onRefresh={() => dispatch(fetchLmsTopics({ class_subject_id: selectedClassSubjectId, chapter_id: selectedChapterId }))}
        />
      )}

      {/* Edit modal */}
      {editTarget && EDIT_CONFIGS[editTarget.type] && (
        <EditModal
          title={EDIT_CONFIGS[editTarget.type].title}
          fields={EDIT_CONFIGS[editTarget.type].fields}
          initial={editTarget.initial}
          onSave={handleEditSave}
          onClose={() => setEditTarget(null)}
          saving={editSaving}
        />
      )}

      {/* Delete confirmation modal */}
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={runConfirmed}
          onCancel={() => setConfirm(null)}
          loading={confirmLoading}
        />
      )}
    </div>
  );
}
